import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule, NgForm, NgModel } from '@angular/forms';
import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { CommonModule, DatePipe } from '@angular/common';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ToastrModule } from 'ngx-toastr';
import { NgSelectModule } from '@ng-select/ng-select';
import { LoaderInterceptor } from './interceptors/loader.interceptors';

import { AppComponent } from './app.component';
import { NavComponent } from './traits/nav/nav.component';
import { FootComponent } from './traits/foot/foot.component';
import { LoaderComponent } from './traits/loader/loader.component';

import { DashboardComponent } from './views/dashboard/dashboard.component';
import { TellerTransferComponent } from './views/teller/teller-transfer/teller-transfer.component';
import { TellerVoucherComponent } from './views/teller/teller-voucher/teller-voucher.component';
import { TellerSummaryComponent } from './views/teller/teller-summary/teller-summary.component';
import { EmployeeComponent } from './views/employee/employee.component';
import { FinanceDaybookComponent } from './views/finance/finance-daybook/finance-daybook.component';
import { FinanceCashbookComponent } from './views/finance/finance-cashbook/finance-cashbook.component';
import { FinancePaymentComponent } from './views/finance/finance-payment/finance-payment.component';
import { FinanceSummaryComponent } from './views/finance/finance-summary/finance-summary.component';
import { FinanceReportComponent } from './views/finance/finance-report/finance-report.component';
import { FinancePayrollComponent } from './views/finance/finance-payroll/finance-payroll.component';
import { SetupSequenceComponent } from './views/setup/setup-sequence/setup-sequence.component';
import { SetupNorminalComponent } from './views/setup/setup-norminal/setup-norminal.component';
import { SetupUserComponent } from './views/setup/setup-user/setup-user.component';
import { SetupSmslogsComponent } from './views/setup/setup-smslogs/setup-smslogs.component';
import { RouterModule, RouterOutlet, Routes } from '@angular/router';
import { appRoute } from './router';
import { LoginComponent } from './views/auth/login/login.component';
import { AddemployeeComponent } from './views/employee/addemployee/addemployee.component';
import { SetupSmsComponent } from './views/setup/setup-sms/setup-sms.component';

import { SigninService } from './services/signin.service';
import { SetupService } from './services/setup.service';
import { AuthGuard } from './guards/auth.guard';
import { ExitpageGuard } from './guards/exitpage.guard';
import { AuthInterceptor } from './interceptors/auth.interceptor';
import { JwtModule } from '@auth0/angular-jwt';
import { NotfoundComponent } from './views/error/notfound/notfound.component';
import { SetupTransitComponent } from './views/setup/setup-transit/setup-transit.component';
import { InventoryService } from './services/inventory.service';
import { TellerService } from './services/teller.service';
import { SetupLocationComponent } from './views/setup/setup-location/setup-location.component';
import { ForgetPasswordComponent } from './views/auth/forget-password/forget-password.component';
import { SetupTellerComponent } from './views/setup/setup-teller/setup-teller.component';
import { FinanceTransferComponent } from './views/finance/finance-transfer/finance-transfer.component';
import { CustomerComponent } from './views/customer/customer.component';
import { AddCustomerComponent } from './views/customer/add-customer/add-customer.component';
import { SetupCompanyComponent } from './views/setup/setup-company/setup-company.component';
import { AlertsComponent } from './views/alerts/alerts.component';
import { TellerPaymentComponent } from './views/teller/teller-payment/teller-payment.component';
import { SetupAccountComponent } from './views/setup/setup-account/setup-account.component';
import { AccountsComponent } from './views/accounts/accounts.component';
import { AddAccountComponent } from './views/accounts/add-account/add-account.component';
import { AccountApprovalComponent } from './views/accounts/account-approval/account-approval.component';
import { ManagerService } from './services/manager.service';
import { SetupCotComponent } from './views/setup/setup-cot/setup-cot.component';
import { ApplyCotComponent } from './views/finance/apply-cot/apply-cot.component';
import { ViewCustomerComponent } from './views/customer/view-customer/view-customer.component';
import { SetupBranchComponent } from './views/setup/setup-branch/setup-branch.component';
import { SetupSessionComponent } from './views/setup/setup-session/setup-session.component';
import { AccountStatusComponent } from './views/accounts/account-status/account-status.component';
import {HashLocationStrategy, LocationStrategy, PathLocationStrategy} from '@angular/common';
import { AccountChequeComponent } from './views/accounts/account-cheque/account-cheque.component';
import { FinanceAccountComponent } from './views/finance/finance-account/finance-account.component';
import { FinanceReceiptComponent } from './views/finance/finance-receipt/finance-receipt.component';
import { TellerBatchComponent } from './views/teller/teller-batch/teller-batch.component';
import { MigrationsComponent } from './views/migrations/migrations.component';
import { RelationOfficersComponent } from './views/relation-officers/relation-officers.component';
import { AddRelationOfficerComponent } from './views/relation-officers/add-relation-officer/add-relation-officer.component';
import { GroupsComponent } from './views/groups/groups.component';
import { AddGroupComponent } from './views/groups/add-group/add-group.component';
import { ViewGroupComponent } from './views/groups/view-group/view-group.component';
import { FinanceFundManagerComponent } from './views/finance/finance-fund-manager/finance-fund-manager.component';
import { AgentsComponent } from './views/agents/agents.component';
import { AddAgentComponent } from './views/agents/add-agent/add-agent.component';
import { CustomerReportComponent } from './views/customer/customer-report/customer-report.component';
import { ChangeRelationOfficerComponent } from './views/relation-officers/change-relation-officer/change-relation-officer.component';
import { AccountReportComponent } from './views/accounts/account-report/account-report.component';
import { GroupReportComponent } from './views/groups/group-report/group-report.component';
import { PaginationComponent } from './traits/pagination/pagination.component';
import { ViewAgentComponent } from './views/agents/view-agent/view-agent.component';
import { SetupChargeComponent } from './views/setup/setup-charge/setup-charge.component';
import { ViewRelationOfficerComponent } from './views/relation-officers/view-relation-officer/view-relation-officer.component';
import { ApprovalsComponent } from './views/approvals/approvals.component';
import { LockComponent } from './views/auth/lock/lock.component';
import { AccountGuard } from './guards/account.guard';
import { AdminGuard } from './guards/admin.guard';
import { SupportGuard } from './guards/support.guard';
import { ManagerGuard } from './guards/manager.guard';
import { SetupPackageComponent } from './views/setup/setup-package/setup-package.component';
import { MotoInsuranceComponent } from './views/finance/moto-insurance/moto-insurance.component';
import { ViewMotoInsuranceComponent } from './views/finance/moto-insurance/view-moto-insurance/view-moto-insurance.component';
import { AccountAutodebitComponent } from './views/accounts/account-autodebit/account-autodebit.component';
import { SetupCountryComponent } from './views/setup/setup-country/setup-country.component';
import { RelationComponent } from './views/accounts/relation/relation.component';
import { BeneficiaryComponent } from './views/accounts/beneficiary/beneficiary.component';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}

export function tokenGetter() {
  return JSON.parse(localStorage.getItem('Token'));
}

@NgModule({
  declarations: [
    AppComponent,
    NavComponent,
    FootComponent,
    PaginationComponent,
    DashboardComponent,
    TellerTransferComponent,
    TellerVoucherComponent,
    TellerSummaryComponent,
    EmployeeComponent,
    FinanceDaybookComponent,
    FinanceCashbookComponent,
    FinancePaymentComponent,
    FinanceSummaryComponent,
    FinanceReportComponent,
    FinancePayrollComponent,
    SetupSequenceComponent,
    SetupNorminalComponent,
    SetupUserComponent,
    SetupSmslogsComponent,
    LoginComponent,
    AddemployeeComponent,
    SetupSmsComponent,
    NotfoundComponent,
    SetupTransitComponent,
    SetupLocationComponent,
    ForgetPasswordComponent,
    SetupTellerComponent,
    FinanceTransferComponent,
    CustomerComponent,
    AddCustomerComponent,
    SetupCompanyComponent,
    AlertsComponent,
    TellerPaymentComponent,
    SetupAccountComponent,
    AccountsComponent,
    AddAccountComponent,
    AccountApprovalComponent,
    SetupCotComponent,
    SetupChargeComponent,
    ApplyCotComponent,
    ViewCustomerComponent,
    SetupBranchComponent,
    SetupSessionComponent,
    AccountStatusComponent,
    AccountChequeComponent,
    FinanceAccountComponent,
    FinanceReceiptComponent,
    LoaderComponent,
    TellerBatchComponent,
    MigrationsComponent,
    RelationOfficersComponent,
    AddRelationOfficerComponent,
    GroupsComponent,
    AddGroupComponent,
    ViewGroupComponent,
    FinanceFundManagerComponent,
    AgentsComponent,
    AddAgentComponent,
    CustomerReportComponent,
    ChangeRelationOfficerComponent,
    AccountReportComponent,
    GroupReportComponent,
    ViewAgentComponent,
    ViewRelationOfficerComponent,
    ApprovalsComponent,
    LockComponent,
    SetupPackageComponent,
    MotoInsuranceComponent,
    ViewMotoInsuranceComponent,
    AccountAutodebitComponent,
    SetupCountryComponent,
    RelationComponent,
    BeneficiaryComponent
  ],
  imports: [
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    HttpClientModule,
    FormsModule,
    CommonModule,
    NgSelectModule,
    JwtModule.forRoot({
      config: {
        tokenGetter: tokenGetter,
      }
    }),
    TranslateModule.forRoot({
      loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient]
      }
    }),
    ToastrModule.forRoot({
      timeOut: 10000,
      positionClass: 'toast-top-right',
      preventDuplicates: false,
      progressBar: true
    }),
    RouterModule.forRoot(appRoute)
  ],
  providers: [ 
    AuthGuard, ManagerGuard, AccountGuard, SupportGuard, AdminGuard, DatePipe,
    ExitpageGuard, ManagerService, SigninService, SetupService, InventoryService, TellerService,
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
    // { provide: LocationStrategy, useClass: HashLocationStrategy } 
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
